import type { ReactNode } from 'react';
import { Button } from './button';
import { AlertIcon, PlugIcon } from './icons';

/**
 * Shared loading / empty / error / not-connected states. Every screen uses these so an absent,
 * unknown, failed or not-yet-connected result is always shown honestly — never replaced by
 * invented values — and always in the same visual language.
 */

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 p-6 text-sm text-muted"
    >
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-line-strong border-t-accent"
      />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="rounded-[var(--radius-card)] border border-dashed border-line-strong bg-surface px-6 py-10 text-center"
    >
      <h2 className="text-base font-semibold text-fg">{title}</h2>
      {description ? (
        <p className="mx-auto mt-2 max-w-md text-sm text-muted">{description}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </section>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex gap-3 rounded-[var(--radius-card)] border border-danger-line bg-danger-soft p-5"
    >
      <AlertIcon className="mt-0.5 size-5 shrink-0 text-danger" />
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-fg">{title}</h2>
        <p className="mt-1 text-sm text-fg-2">{message}</p>
        {onRetry ? (
          <Button variant="secondary" className="mt-4" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Something the portal does not collect yet. Calm and intentional — not an error: the capability
 * simply has not been connected. Never shows a placeholder figure.
 */
export function NotConnectedState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex gap-4 rounded-[var(--radius-card)] border border-line bg-surface p-5 sm:p-6">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-raised text-muted">
        <PlugIcon />
      </span>
      <div className="min-w-0">
        <p className="text-base font-semibold text-fg">{title}</p>
        {children ? <div className="mt-1 text-sm text-muted">{children}</div> : null}
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    </div>
  );
}
